import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import CreateTenancyFlow from './CreateTenancyFlow'
import InvitationAcceptanceFlow from './InvitationAcceptanceFlow'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6200'

export default function TenancyOnboardingStep() {
  const { token } = useAuth()
  const [view, setView] = useState('loading') // 'loading', 'choice', 'create', 'invitations'
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchPendingInvitations()
  }, [])

  const fetchPendingInvitations = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_URL}/api/v1/invitations/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.invitations && data.invitations.length > 0) {
          setInvitations(data.invitations)
          setView('invitations')
        } else {
          setView('choice')
        }
      } else {
        // If endpoint fails, just show choice
        setView('choice')
      }
    } catch (err) {
      console.error('Failed to fetch invitations:', err)
      setView('choice')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-8">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-[#617589] text-sm">Checking for invitations...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-8">
        {view === 'invitations' && (
          <InvitationAcceptanceFlow
            invitations={invitations}
            onCreateInstead={() => setView('create')}
          />
        )}

        {view === 'choice' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <span className="material-symbols-outlined text-primary text-4xl">business_center</span>
              </div>
              <h3 className="text-[#111418] text-2xl font-bold mb-2">
                Let's get you started!
              </h3>
              <p className="text-[#617589] text-base">
                Choose how you want to begin
              </p>
            </div>

            {/* Options */}
            <div className="space-y-4">
              <button
                onClick={() => setView('create')}
                className="w-full p-6 border-2 border-[#dbe0e6] rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                    <span className="material-symbols-outlined text-primary text-2xl">add_business</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-[#111418] text-lg font-bold mb-1">Create New Organization</h4>
                    <p className="text-[#617589] text-sm leading-relaxed">
                      Start fresh with your own workspace
                    </p>
                  </div>
                  <span className="material-symbols-outlined text-[#617589] text-2xl">arrow_forward</span>
                </div>
              </button>

              <button
                onClick={() => setView('join')}
                className="w-full p-6 border-2 border-[#dbe0e6] rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <span className="material-symbols-outlined text-blue-600 group-hover:text-primary text-2xl">group_add</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-[#111418] text-lg font-bold mb-1">Join Existing Organization</h4>
                    <p className="text-[#617589] text-sm leading-relaxed">
                      You need an invitation link or join code
                    </p>
                  </div>
                  <span className="material-symbols-outlined text-[#617589] text-2xl">arrow_forward</span>
                </div>
              </button>
            </div>
          </div>
        )}

        {view === 'create' && (
          <CreateTenancyFlow onBack={() => setView('choice')} />
        )}

        {view === 'join' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center">
              <h3 className="text-[#111418] text-2xl font-bold mb-2">
                Join Organization
              </h3>
              <p className="text-[#617589] text-base">
                Enter your invitation details
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined text-blue-600 text-[20px] mt-0.5">info</span>
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-1">Need an invitation?</p>
                  <p>Ask your administrator to send you an invitation email. You can click the link in the email to join automatically.</p>
                </div>
              </div>
            </div>

            {/* Join Code Input (Future Feature) */}
            <div className="text-center py-8">
              <p className="text-[#617589] text-sm mb-4">
                Or enter a join code if you have one
              </p>
              <input
                className="form-input w-full max-w-sm mx-auto text-center tracking-wider text-lg font-mono resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white h-12 px-4 placeholder:text-[#617589] focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
                placeholder="XXXXX-XXXXX-XXXXX"
                disabled
              />
              <p className="text-xs text-[#617589] mt-2">
                Join codes coming soon
              </p>
            </div>

            {/* Back Button */}
            <button
              onClick={() => setView('choice')}
              className="w-full text-[#617589] hover:text-[#111418] text-sm font-medium transition-colors py-2"
            >
              ← Back
            </button>
          </div>
        )}

        {/* Footer Note */}
        <p className="text-xs text-[#617589] text-center mt-6">
          Step 2 of 2: Set up your workspace
        </p>
      </div>
    </div>
  )
}
